import { useMinistries } from '@/features/master-table/hooks';
import {
  ActivityFlowSection,
  KsAdditionalInfoSection,
  KsAdditionalInfoSectionBefore2025,
  KsBasicInfoSection,
  KsBasicInfoSectionBefore2025,
  KsFundAdditionalIncomeAndExpenditureInfoSection,
  KsFundAdditionalIncomeAndExpenditureInfoSectionBefore2025,
  KsFundBusinessInfoSection,
  KsInspectionEvaluationSection,
  KsInspectionEvaluationSectionBefore2025,
  KsOwnershipRatioSection,
  LedgerPage,
  PaymentSection,
  RelatedMaterialSection,
} from '@/features/mold/components';
import {
  PAPER_FILE_SIZE,
  getConcatenatedBusinessManagerName,
} from '@/features/mold/utils';
import {
  useActivityTreeOptions,
  useNestedConsecutiveIndicators,
} from '@/features/project/_hooks/activity';
import { useBusinessManagers } from '@/features/project/_hooks/business-managers';
import { useFundBusinesses } from '@/features/project/_hooks/fund-business';
import { useProjectHasRelatedOrganizations } from '@/features/project/_hooks/organization';
import { useContractorPayments } from '@/features/project/_hooks/payment';
import { usePaymentGraph } from '@/features/project/_hooks/payment-graph';
import { usePaymentGroups } from '@/features/project/_hooks/payment-group';
import { useProject } from '@/features/project/_hooks/project';
import { useRelatedMaterials } from '@/features/project/_hooks/related-material';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { useFilteredUsers } from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { uniq } from 'lodash-es';
import {
  calcAdministrativeExpensesRate,
  convertPriorToList,
  getClericalExpenditureSummaryByType,
  getExpenditureSummaryByType,
  getIncomeAndExpenditureSummaryByType,
  getIncomeSummaryByType,
} from '../../expenditure/_utils/fund-income-and-expenditure-table';

export const KsProjectLedgerPage = () => {
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const projectOrganizations = useProjectHasRelatedOrganizations({
    projectId,
    params: {
      showMigrated: true,
      historySavedAt: project.data?.historySavedAt,
    },
    enabled: !!projectId,
  });
  const ministries = useMinistries();
  const concatenatedBusinessManagerName = useBusinessManagers({
    projectId,
    select: getConcatenatedBusinessManagerName,
  });
  const additionalJurisdictionalBusinessManagers = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids:
        project.data?.fundAdditionalAttributes?.additionalJurisdictionalUsers?.flatMap(
          (v) => v.businessManagerIds,
        ) ?? [],
      historySavedAt: project.data?.historySavedAt,
      showMigrated: true,
    },
  });

  const fundBusinesses = useFundBusinesses({ projectId });
  const activityTreeOption = useActivityTreeOptions(projectId);
  const routeIndicatorIds = uniq(
    activityTreeOption.selectOptions.flatMap((option) =>
      option.routeIndicators.flatMap((v) => v.indicator.id),
    ),
  );
  const nestedConsecutiveIndicators =
    useNestedConsecutiveIndicators(routeIndicatorIds);
  const relatedMaterials = useRelatedMaterials(projectId);
  const paymentGroups = usePaymentGroups({ projectId });
  const paymentGraph = usePaymentGraph(projectId);
  const payments = useContractorPayments({ projectId });

  const isLoading =
    project.isLoading ||
    ministries.isLoading ||
    projectOrganizations.isLoading ||
    !!(project.data?.fundAdditionalAttributes?.additionalJurisdictionalUsers
      ?.length
      ? additionalJurisdictionalBusinessManagers.isLoading
      : false) ||
    concatenatedBusinessManagerName.isLoading ||
    payments.isLoading ||
    paymentGraph.isLoading ||
    relatedMaterials.isLoading ||
    paymentGroups.isLoading ||
    fundBusinesses.isLoading;

  return (
    <LedgerPage width={PAPER_FILE_SIZE.A4.height} isLoading={isLoading}>
      {isBefore2025(project.data) ? (
        <KsBasicInfoSectionBefore2025
          project={project.data}
          ministries={ministries.data}
          organizations={projectOrganizations.data}
          additionalJurisdictionalBusinessManagers={
            additionalJurisdictionalBusinessManagers.data?.results
          }
          concatenatedBusinessManagerName={concatenatedBusinessManagerName.data}
        />
      ) : (
        <KsBasicInfoSection
          project={project.data}
          ministries={ministries.data}
          organizations={projectOrganizations.data}
          additionalJurisdictionalBusinessManagers={
            additionalJurisdictionalBusinessManagers.data?.results
          }
          concatenatedBusinessManagerName={concatenatedBusinessManagerName.data}
        />
      )}

      {isBefore2025(project.data) ? (
        <KsAdditionalInfoSectionBefore2025 project={project.data} />
      ) : (
        <KsAdditionalInfoSection project={project.data} />
      )}
      {isBefore2025(project.data) ? (
        <>
          <KsFundAdditionalIncomeAndExpenditureInfoSectionBefore2025
            project={project.data}
            utils={{
              convertPriorToList,
              calcAdministrativeExpensesRate,
              getClericalExpenditureSummaryByType,
              getExpenditureSummaryByType,
              getIncomeAndExpenditureSummaryByType,
              getIncomeSummaryByType,
            }}
          />
          <ActivityFlowSection
            project={project.data}
            nestedConsecutiveIndicators={nestedConsecutiveIndicators.data}
            tree={activityTreeOption.tree.data}
            selectOptions={activityTreeOption.selectOptions}
          />
        </>
      ) : (
        <>
          <ActivityFlowSection
            project={project.data}
            nestedConsecutiveIndicators={nestedConsecutiveIndicators.data}
            tree={activityTreeOption.tree.data}
            selectOptions={activityTreeOption.selectOptions}
          />
          <KsFundAdditionalIncomeAndExpenditureInfoSection
            project={project.data}
            utils={{
              convertPriorToList,
              calcAdministrativeExpensesRate,
              getClericalExpenditureSummaryByType,
              getExpenditureSummaryByType,
              getIncomeAndExpenditureSummaryByType,
              getIncomeSummaryByType,
            }}
          />
        </>
      )}
      <KsFundBusinessInfoSection
        fiscalYear={project.data?.fiscalYear}
        fundBusinesses={fundBusinesses.data}
      />
      {!isBefore2025(project.data) && (
        <KsOwnershipRatioSection project={project.data} />
      )}
      {isBefore2025(project.data) ? (
        <KsInspectionEvaluationSectionBefore2025 project={project.data} />
      ) : (
        <KsInspectionEvaluationSection project={project.data} />
      )}
      <PaymentSection
        project={project.data}
        paymentGraph={paymentGraph.data}
        paymentGroups={paymentGroups.data}
        payments={payments.data}
      />
      <RelatedMaterialSection relatedMaterials={relatedMaterials.data} />
    </LedgerPage>
  );
};
